import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WaitingLobby from '../components/WaitingLobby';
import { getApiBaseUrl } from '../utils/apiBase';
import './LandingPage.css';

const API_URL = getApiBaseUrl();

const LANGUAGES = [
  {
    id   : 'sv-en',
    title: 'English',
    desc : 'The demo runs in English.',
  },
  {
    id   : 'sv',
    title: 'Svenska',
    desc : 'Demon guidar dig på svenska.',
  },
];

function LandingPage() {
  const [step,           setStep]           = useState('email');
  const [email,          setEmail]          = useState('');
  const [language,       setLanguage]       = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [roomData,       setRoomData]       = useState(null);
  const [sessionId,      setSessionId]      = useState(null);
  const [botStatus,      setBotStatus]      = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [checkingEmail, setCheckingEmail]   = useState(false);

  useEffect(() => {
    checkBotStatus();
    const interval = setInterval(() => {
      if (botStatus?.isBusy) checkBotStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [botStatus?.isBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkBotStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/sessions/bot-status`);
      setBotStatus(res.data);
    } catch (err) {
      console.error('Error checking bot status:', err);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleEmailNext = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setCheckingEmail(true);
    try {
      const prevPreset = sessionStorage.getItem('demo_preset_session_id') || '';
      const { data } = await axios.post(`${API_URL}/api/sessions/check-email`, {
        email,
        ...(prevPreset ? { replaceSessionId: prevPreset } : {}),
      });
      if (data.sessionId) {
        sessionStorage.setItem('demo_preset_session_id', data.sessionId);
      }
      if (data.portalId) {
        sessionStorage.setItem('demo_portal_id', data.portalId);
      }
      setStep('language');
    } catch (err) {
      if (err.response?.status === 403) {
        setError(
          err.response?.data?.error ||
            'This email is not authorized for this demo. Use the work email you were invited with, or contact your administrator.'
        );
      } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError(
          'Cannot reach the demo server (browser blocked the request — often CORS or mixed content). ' +
            'On HTTPS, do not bake REACT_APP_API_URL=http://localhost:5000 into the build; leave it empty and proxy /api to the backend, ' +
            'or set REACT_APP_API_URL to your real HTTPS API. The API must allow this origin in ALLOWED_ORIGINS (see backend server.js).'
        );
      } else {
        setError(
          err.response?.data?.error ||
            'Could not verify email. Check your connection and try again.'
        );
      }
    } finally {
      setCheckingEmail(false);
    }
  };

  const emailErrorIsUnauthorized =
    error && /authorized|access denied|not authorized/i.test(error);

  const handleContinue = async () => {
    if (!language) return;
    setError('');
    setLoading(true);
    sessionStorage.setItem('demo_language', language);

    try {
      const presetSessionId = sessionStorage.getItem('demo_preset_session_id') || '';
      const storedPortalId = sessionStorage.getItem('demo_portal_id') || null;
      const response = await axios.post(`${API_URL}/api/sessions`, {
        email,
        platform: 'livekit',
        language,
        ...(presetSessionId ? { sessionId: presetSessionId } : {}),
        ...(storedPortalId ? { portalId: storedPortalId } : {}),
      });
      const { livekitUrl, livekitRoom, livekitToken, session, portalId } = response.data;

      sessionStorage.removeItem('demo_preset_session_id');

      // Store the portal ID returned by the backend (auto-assigned from env if not specified).
      // WaitingLobby reads this from sessionStorage to include ?portalId= in the redirect URL.
      if (portalId) {
        sessionStorage.setItem('demo_portal_id', portalId);
        // Do not block redirect: metadata is only for header labels on the meeting page.
        axios.get(`${API_URL}/api/portals/${portalId}`).then(({ data: portalData }) => {
          if (portalData?.portal) {
            sessionStorage.setItem('demo_portal_url',  portalData.portal.url  || '');
            sessionStorage.setItem('demo_portal_name', portalData.portal.name || '');
          }
        }).catch(() => {});
      } else {
        sessionStorage.removeItem('demo_portal_id');
        sessionStorage.removeItem('demo_portal_url');
        sessionStorage.removeItem('demo_portal_name');
      }

      setSessionId(session?.id || null);
      setRoomData({ url: livekitUrl, room: livekitRoom, token: livekitToken });
    } catch (err) {
      console.error('❌ Error:', err);
      if (err.response?.status === 409) {
        await checkBotStatus();
        setError('All demo slots are currently in use. Please wait.');
        setStep('language');
      } else if (err.response?.status === 403) {
        setError(
          err.response?.data?.error ||
            'Access denied. This demo is restricted to authorized email addresses.'
        );
        setStep('email');
      } else {
        setError(
          err.response?.data?.error  ||
          err.response?.data?.message ||
          'Something went wrong. Please try again.'
        );
        setStep('language');
      }
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      {loading && (
        <WaitingLobby
          roomData={roomData}
          sessionId={sessionId}
        />
      )}

      <div className="container">
        <header className="header">
          <span className="logo">Custio Interactive Demo</span>
          <a className="powered-by" href="https://withhilda.com" target="_blank" rel="noreferrer">Powered by Hilda</a>
        </header>

        <main className="main-content">
          <div className="hero">

            {/* ── Step 1: Email ─────────────────────────────────────────── */}
            {step === 'email' && (
              <>
                <p className="step-indicator">Step 1 of 2</p>
                <h2 className="title">Try Custio live</h2>
                <p className="subtitle">
                  Explore a real Custio demo with real clicks. Ask questions as you go and see how the platform works in practice.
                </p>

                {checkingStatus ? (
                  <div className="status-check">
                    <div className="spinner" />
                    <p>Checking availability…</p>
                  </div>
                ) : botStatus?.isBusy || botStatus?.botBusy ? (
                  <div className="inline-busy-message">
                    <h3 className="busy-title-inline">All Demo Slots Are Full</h3>
                    <p className="busy-text">
                      All {botStatus?.maxConcurrent || ''} demo slots are currently in use.
                      Please wait a moment and try again.
                    </p>
                    <button className="refresh-button-inline" onClick={checkBotStatus}>
                      ↻ Check Again
                    </button>
                    <p className="busy-footer-inline">
                      A slot will open automatically when a session ends.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleEmailNext} className="demo-form">
                    {error && (
                      <div
                        id="email-step-alert"
                        className={`email-step-alert ${emailErrorIsUnauthorized ? 'email-step-alert--unauthorized' : ''}`}
                        role="alert"
                        aria-live="assertive"
                      >
                        {emailErrorIsUnauthorized && (
                          <span className="email-step-alert__title">Unauthorized email</span>
                        )}
                        <p className="email-step-alert__body">{error}</p>
                      </div>
                    )}

                    <div className="input-group">
                      <label className="input-label" htmlFor="email-input">Work email</label>
                      <input
                        id="email-input"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError('');
                        }}
                        className={`email-input${error ? ' email-input--invalid' : ''}`}
                        disabled={loading || checkingEmail}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={error ? 'email-step-alert' : undefined}
                        required
                      />
                      <p className="input-hint">
                        Only authorized work emails can access this demo. Use the email you were invited with.
                      </p>
                    </div>

                    <button type="submit" className="cta-button" disabled={loading || checkingEmail}>
                      {checkingEmail ? 'Checking…' : 'Continue'}
                    </button>
                  </form>
                )}
              </>
            )}

            {/* ── Step 2: Language picker ───────────────────────────────── */}
            {step === 'language' && (
              <>
                <p className="step-indicator">Step 2 of 2</p>
                <h2 className="title">Choose your demo language</h2>
                <p className="subtitle">
                  Pick the language you want the demo guide to use.
                </p>

                <div className="language-step">
                  <div className="language-options">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.id}
                        className={`language-option ${language === lang.id ? 'selected' : ''}`}
                        onClick={() => setLanguage(lang.id)}
                        disabled={loading}
                      >
                        <span className="lang-radio">
                          <span className="lang-radio-dot" />
                        </span>
                        <span className="lang-text">
                          <span className="lang-title">{lang.title}</span>
                          <span className="lang-desc">{lang.desc}</span>
                        </span>
                      </button>
                    ))}
                  </div>

                  <button
                    className="cta-button"
                    onClick={handleContinue}
                    disabled={!language || loading}
                  >
                    {loading ? 'Starting demo…' : 'Start live demo'}
                  </button>

                  {error && <p className="error-message">{error}</p>}

                  <p className="reassurance-text">
                    You'll enter a live demo environment with guided assistance.
                  </p>

                  <button
                    className="back-link"
                    onClick={() => { setStep('email'); setError(''); setLanguage(null); }}
                    disabled={loading}
                  >
                    ← Back
                  </button>
                </div>
              </>
            )}

          </div>
        </main>

        <footer className="footer">
          <p>Demo environment only. No setup required.</p>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
